import {
  IMAGE_MAX_BYTES,
  IMAGE_MAX_EDGE,
  IMAGE_ORIGIN,
  datasheetKey,
  imageKey,
  type ImageSize,
} from './shared/images'

/**
 * Production files load from the bucket's own domain; under `vite dev` the local
 * bucket has no public address, so the Worker serves it at /img instead.
 */
const fileOrigin = () => (import.meta.env.DEV ? '/img' : IMAGE_ORIGIN)

export function productImageUrl(image: string, size: ImageSize): string {
  return `${fileOrigin()}/${imageKey(image, size)}`
}

/** The product's technical data sheet, a PDF opened in its own tab. */
export function productDatasheetUrl(datasheet: string): string {
  return `${fileOrigin()}/${datasheetKey(datasheet)}`
}

export type ImageErrorCode = 'unreadable' | 'unsupported' | 'tooLarge'

export class ImageConversionError extends Error {
  constructor(readonly code: ImageErrorCode) {
    super(code)
    this.name = 'ImageConversionError'
  }
}

export interface ConvertedImage {
  lg: Blob
  sm: Blob
  width: number
  height: number
}

/**
 * Turns whatever the admin picked (JPG, PNG, WebP, …) into the two WebP files the
 * store serves, scaled to fit IMAGE_MAX_EDGE and never enlarged. Runs in the
 * browser, so the upload is small and the Worker never decodes an image.
 */
export async function convertToWebp(file: File): Promise<ConvertedImage> {
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new ImageConversionError('unreadable')
  })
  try {
    const lg = draw(bitmap, IMAGE_MAX_EDGE.lg)
    // The small size is scaled from the large one: one big step down from a
    // camera-sized photo aliases visibly, two smaller ones do not.
    const sm = draw(lg, IMAGE_MAX_EDGE.sm)
    const [lgBlob, smBlob] = await Promise.all([encode(lg, 0.85), encode(sm, 0.8)])
    return { lg: lgBlob, sm: smBlob, width: lg.width, height: lg.height }
  } finally {
    bitmap.close()
  }
}

function draw(source: ImageBitmap | HTMLCanvasElement, maxEdge: number): HTMLCanvasElement {
  const scale = Math.min(1, maxEdge / Math.max(source.width, source.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(source.width * scale))
  canvas.height = Math.max(1, Math.round(source.height * scale))
  const context = canvas.getContext('2d')
  if (!context) throw new ImageConversionError('unsupported')
  context.imageSmoothingQuality = 'high'
  context.drawImage(source, 0, 0, canvas.width, canvas.height)
  return canvas
}

async function encode(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
  // Browsers that cannot encode WebP (Safari) silently hand back a PNG instead.
  if (!blob || blob.type !== 'image/webp') throw new ImageConversionError('unsupported')
  if (blob.size > IMAGE_MAX_BYTES) throw new ImageConversionError('tooLarge')
  return blob
}
